import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AnesthesiologistsView from './AnesthesiologistsView.tsx';
import CrnaView from './CrnaView.tsx';
import SegmentedRadioGroup from './SegmentedRadioGroup.tsx';
import { useWorkingDate } from '../contexts/WorkingDateContext.tsx';

/**
 * Manpower: who is where on the working day. One page for the two staff views that used to be separate
 * tabs (Anesthesiologists, CRNAs) — pick which with the radio at the top, search either by name, and
 * change the day with the same date the Board uses.
 *
 * The choice lives in the URL (`?view=crnas`) so it can be linked and bookmarked; with no `view` in the
 * URL it reopens on whichever view was used last. Only the selected view is mounted. Both read the shared
 * data cache, so switching between them (or to and from the Board) does not refetch the day.
 *
 * Planning the roster itself (creating, editing and deleting shifts) is the Shifts tab.
 */

export type ManpowerViewKey = 'anesthesiologists' | 'crnas';

export const MANPOWER_VIEW_OPTIONS = [
  { value: 'anesthesiologists', label: 'Anesthesiologists' },
  { value: 'crnas', label: 'CRNAs', title: 'CRNAs and student nurse anesthetists (SRNAs)' },
] as const;

export const MANPOWER_LAST_VIEW_KEY = 'oc-manpower-view';

export const parseManpowerView = (raw: string | null | undefined): ManpowerViewKey | null =>
  raw === 'anesthesiologists' || raw === 'crnas' ? raw : null;

const loadLastView = (): ManpowerViewKey => {
  try { return parseManpowerView(localStorage.getItem(MANPOWER_LAST_VIEW_KEY)) ?? 'anesthesiologists'; } catch { return 'anesthesiologists'; }
};

const ManpowerView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const view: ManpowerViewKey = parseManpowerView(searchParams.get('view')) ?? loadLastView();
  const [date, setDate] = useWorkingDate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    try { localStorage.setItem(MANPOWER_LAST_VIEW_KEY, view); } catch { /* localStorage unavailable */ }
  }, [view]);

  const selectView = (next: ManpowerViewKey) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    // replace: flipping the radio is not a navigation the Back button should have to undo step by step.
    setSearchParams(params, { replace: true });
  };

  const viewLabel = view === 'crnas' ? 'CRNAs' : 'anesthesiologists';

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <h1 style={{ margin: 0 }}>Manpower</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label htmlFor="manpowerDate" style={{ whiteSpace: 'nowrap', margin: 0 }}>Date:</label>
          <input
            id="manpowerDate"
            type="date"
            className="form-control"
            style={{ maxWidth: '180px' }}
            value={date}
            onChange={e => setDate(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <SegmentedRadioGroup<ManpowerViewKey>
          legend="Show"
          name="manpower-view"
          options={MANPOWER_VIEW_OPTIONS}
          value={view}
          onChange={selectView}
        />
        <div style={{ flex: '1 1 260px', minWidth: '220px' }}>
          <label htmlFor="manpowerSearch" className="visually-hidden">Search {viewLabel} by name</label>
          <input
            id="manpowerSearch"
            type="search"
            className="form-control"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') setQuery(''); }}
            placeholder={`🔍 Search ${viewLabel} by name...`}
            autoComplete="off"
          />
        </div>
      </div>

      {view === 'crnas'
        ? <CrnaView embedded nameFilter={query} />
        : <AnesthesiologistsView embedded nameFilter={query} />}
    </div>
  );
};

export default ManpowerView;
